<template>
  <div class="class-event-form w-full">
    <div class="md-layout mb-4">
      <div class="md-layout-item md-size-50 md-small-size-100 z-10">
        <isis-autocomplete
          v-model="className"
          :value.sync="eventCfg.classId"
          :options.sync="classOpts"
          :refetchOptions="getClassOpts"
          :fields="{ text: 'name', value: 'id' }"
          :fetchOnOpen="true"
          search-key="name__~"
          :label="$t('schedule.entering_class_name')"
        />
      </div>
      <div class="md-layout-item md-size-30 md-small-size-100">
        <md-field>
          <label>{{ $t("schedule.repeat") }}</label>
          <md-select
            v-model="eventCfg.repeat"
            name="repeatOpts"
            id="repeatOpts"
          >
            <md-option
              v-for="(opt, idx) in repeatOpts"
              :value="opt.value"
              :key="'opt' + idx"
            >
              {{ opt.name }}
            </md-option>
          </md-select>
        </md-field>
      </div>
      <div class="md-layout-item md-size-20 md-small-size-100">
        <md-switch v-model="eventCfg.isAllDay" class="isis-switch-field">
          {{ $t("schedule.all_day") }}
        </md-switch>
      </div>
      <!-- DATE RANGE -->
      <div
        v-if="!isIrregularMode"
        class="md-layout-item md-size-70 md-small-size-100"
      >
        <div class="time-range flex w-full">
          <date-picker-custom
            v-model="eventCfg.start"
            :label="$t('common.start_date')"
          ></date-picker-custom>
          <span class="linking-word text-center">
            {{ $t("common.to") }}
          </span>
          <date-picker-custom
            v-model="eventCfg.end"
            :label="$t('common.end_date')"
          ></date-picker-custom>
        </div>
      </div>
      <!-- DATE RANGE -->

      <!-- TIME ZONE -->
      <div
        class="md-layout-item md-size-30 md-small-size-100 z-10"
        v-if="!eventCfg.isAllDay"
      >
        <isis-autocomplete
          v-model="tzDisplayValue"
          :value.sync="eventCfg.timezone"
          :options="timeZoneOpts"
          :label="$t('timezone.timezone')"
        >
        </isis-autocomplete>
      </div>
      <!-- TIME ZONE -->

      <div class="md-layout-item md-size-50 md-small-size-100">
        <md-field>
          <label>{{ $t("common.description") }}</label>
          <md-input v-model="eventCfg.description"></md-input>
        </md-field>
      </div>

      <div
        v-if="shouldDisplayRepeatSchedule"
        class="md-layout-item md-size-100"
      >
        <repeat-times
          :mode="eventCfg.repeat"
          :is-all-day="eventCfg.isAllDay"
          :repeat-on.sync="eventCfg.repeatOn"
          :is-schedule-editing="isScheduleEditing"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { REPEAT_TIMES, SCHEDULE_TYPES } from "../../event-utils";
import { DAYS_IN_WEEK } from "@/constants";
import { formatDateClient, getTimeString } from "@/helpers/datetime-helper";
import RepeatTimes from "./RepeatTimes";
import { convertObj } from "@/helpers/utils";
import cloneDeep from "lodash/cloneDeep";
import filter from "lodash/filter";
import { service_paths } from "@/constants/Services";
import { TIMEZONE_OPTION } from "@/constants/Timezone";
import moment from "moment-timezone";
import { store } from "@/store";

const initEventCfg = type => {
  const baseEventCfg = {
    classId: "",
    repeat: type,
    timezone: "",
    isAllDay: false,
    start: "",
    end: "",
    description: ""
  };

  if (type !== REPEAT_TIMES.Daily) {
    return {
      ...baseEventCfg,
      repeatOn: []
    };
  }

  return {
    ...baseEventCfg,
    toTime: "",
    fromTime: "",
    location: ""
  };
};

const genWeeklyRepeatOn = (start, event) => {
  const { end, repeatOn } = event;

  const DAY_NUMER_OF_WEEK = 7;
  const dayNumber = start
    ? moment(end).diff(moment(start), "days")
    : DAY_NUMER_OF_WEEK;

  const isEditOldSchedule = !!repeatOn;

  let allowedRepeatDays = [];

  if (dayNumber >= 7) {
    allowedRepeatDays = DAYS_IN_WEEK;
  } else {
    const startDate = moment(start);

    while (startDate.isSameOrBefore(moment(end), "date")) {
      const dayObject = {
        label: startDate.format("ddd"),
        value: startDate.format("dddd")
      };
      allowedRepeatDays.push(dayObject);
      startDate.add(1, "days");
    }
  }

  return allowedRepeatDays.map(repeatObj => {
    const savedRepeat = isEditOldSchedule
      ? repeatOn.find(savedRepeat => savedRepeat.dayOfWeek === repeatObj.value)
      : null;

    if (!savedRepeat) {
      return {
        toTime: "",
        fromTime: "",
        location: "",
        dayOfWeek: repeatObj
      };
    }

    return {
      ...savedRepeat,
      dayOfWeek: repeatObj
    };
  });
};

const genIrregularRepeatOn = (start, savedEvent) => {
  if (!savedEvent) {
    return [
      {
        toTime: "",
        fromTime: "",
        date: "",
        location: ""
      }
    ];
  }

  const { repeatOn: repeatOnList } = savedEvent;

  const isEditingFollowings = !!start;

  const validRepeatOnList = isEditingFollowings
    ? repeatOnList.filter(savedRepeatOn =>
        moment(savedRepeatOn.start).isSameOrAfter(start)
      )
    : repeatOnList;

  return validRepeatOnList.map(savedRepeatOn => {
    // Note: because is irregular schedule, so date = startDate
    const [startDate, startTime] = savedRepeatOn.start.split(" ");
    const [, endTime] = savedRepeatOn.end.split(" ");

    return {
      fromTime: startTime,
      toTime: endTime,
      date: startDate,
      location: savedRepeatOn.location
    };
  });
};

const generateDailyRepeatOn = savedEvent => ({
  fromTime: savedEvent.fromTime,
  toTime: savedEvent.toTime,
  location: savedEvent.location
});

const generateRepeatOn = (start, savedEvent) => {
  if (savedEvent.repeat === REPEAT_TIMES.Weekly) {
    return genWeeklyRepeatOn(start, savedEvent);
  }

  if (savedEvent.repeat === REPEAT_TIMES.Irregularly) {
    return genIrregularRepeatOn(start, savedEvent);
  }

  return generateDailyRepeatOn(savedEvent);
};

export default {
  name: "class-schedule-form",
  components: { RepeatTimes },
  props: ["isisEvent", "calendarEvent", "editingType"],
  data() {
    const isEditingFollowings =
      this.editingType === SCHEDULE_TYPES.chainEvent.value;
    const repeatOpts = Object.keys(REPEAT_TIMES).map(key => ({
      name: key,
      value: REPEAT_TIMES[key]
    }));

    const defaultEventCfg = !!this.isisEvent
      ? convertObj.snakeCaseToCamelCase(this.isisEvent.raw_data)
      : initEventCfg(REPEAT_TIMES.Weekly);

    const className = this.isisEvent?.class?.name ?? "";

    /**
     * ? Start time can be get from:
     * ? Calendar <- when client choose option: This and following (because irregular event doesn't have start-end)
     * ? Saved event <- when client choose: All schedule
     */
    const startTime = isEditingFollowings
      ? getTimeString(
          this.calendarEvent.startStr,
          defaultEventCfg.timezone,
          `${formatDateClient} HH:mm`
        )
      : defaultEventCfg.start;

    defaultEventCfg.repeatOn = generateRepeatOn(startTime, defaultEventCfg);

    const timeZone = store.getters["user/timezone"];

    return {
      className,
      classOpts: [],
      repeatOpts,
      timeZoneOpts: TIMEZONE_OPTION,
      eventCfg: defaultEventCfg,
      tzDisplayValue: defaultEventCfg.timezone || timeZone
    };
  },
  computed: {
    isIrregularMode() {
      return this.eventCfg.repeat === REPEAT_TIMES.Irregularly;
    },
    isWeeklyMode() {
      return this.eventCfg.repeat === REPEAT_TIMES.Weekly;
    },
    isDailyMode() {
      return this.eventCfg.repeat === REPEAT_TIMES.Daily;
    },
    isAllDay() {
      return this.eventCfg.isAllDay;
    },
    isEditingMode() {
      return !!this.isisEvent;
    },
    isScheduleEditing() {
      return (
        this.isEditingMode &&
        this.editingType !== SCHEDULE_TYPES.singleEvent.value
      );
    },
    shouldDisplayRepeatSchedule() {
      if (this.eventCfg.repeat === REPEAT_TIMES.Weekly) {
        return (
          this.eventCfg.start &&
          this.eventCfg.end &&
          moment(this.eventCfg.end).isSameOrAfter(this.eventCfg.start)
        );
      }

      return true;
    }
  },
  watch: {
    "eventCfg.repeat": function() {
      if (this.isWeeklyMode) {
        this.eventCfg.repeatOn = genWeeklyRepeatOn(this.eventCfg.start, {
          end: this.eventCfg.end
        });
      }
      if (this.isDailyMode) {
        this.eventCfg.repeatOn = {
          toTime: "",
          fromTime: "",
          location: ""
        };
      }
      if (this.isIrregularMode) {
        this.eventCfg.repeatOn = genIrregularRepeatOn();
      }
    },
    "eventCfg.start": function(newStartDate) {
      if (this.isWeeklyMode) {
        this.eventCfg.repeatOn = genWeeklyRepeatOn(newStartDate, {
          end: this.eventCfg.end
        });
      }
    },
    "eventCfg.end": function(newEndDate) {
      if (this.isWeeklyMode) {
        this.eventCfg.repeatOn = genWeeklyRepeatOn(this.eventCfg.start, {
          end: newEndDate
        });
      }
    }
  },
  created() {
    this.getClassOpts({
      "name__~": this.className
    });
  },
  methods: {
    getWeeklyEventCfg() {
      const classSchedule = cloneDeep(this.eventCfg);
      const repeatOn = filter(
        classSchedule.repeatOn,
        repeat => repeat.isActivated
      );
      classSchedule.repeatOn = repeatOn.map(repeat => {
        return {
          ...repeat,
          dayOfWeek: repeat.dayOfWeek.value
        };
      });
      return convertObj.camelCaseToSnakeCase(classSchedule);
    },

    getDailyEventCfg() {
      const classSchedule = cloneDeep(this.eventCfg);
      classSchedule.fromTime = classSchedule.repeatOn.fromTime;
      classSchedule.toTime = classSchedule.repeatOn.toTime;
      delete classSchedule.repeatOn;
      return convertObj.camelCaseToSnakeCase(classSchedule);
    },

    getIrregularEventCfg() {
      const classSchedule = cloneDeep(this.eventCfg);
      const repeatOn = filter(classSchedule.repeatOn, function(repeat) {
        return (
          repeat.date || repeat.fromTime || repeat.toTime || repeat.location
        );
      });
      classSchedule.repeatOn = repeatOn.map(repeat => {
        return {
          start: this.isAllDay
            ? repeat.date
            : `${repeat.date} ${repeat.fromTime}`,
          end: this.isAllDay ? repeat.date : `${repeat.date} ${repeat.toTime}`,
          location: repeat.location
        };
      });
      delete classSchedule.start;
      delete classSchedule.end;
      return convertObj.camelCaseToSnakeCase(classSchedule);
    },

    getEventCfg() {
      if (this.isWeeklyMode) {
        return this.getWeeklyEventCfg();
      }
      if (this.isDailyMode) {
        return this.getDailyEventCfg();
      }
      if (this.isIrregularMode) {
        return this.getIrregularEventCfg();
      }
    },

    getClassOpts(queryObj) {
      return this.$axios
        .get(
          service_paths.classes,
          queryObj,
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(({ data: resData }) => {
          this.classOpts = resData?.data;
        });
    },

    resetEventCfg() {
      this.eventCfg = initEventCfg();
    }
  }
};
</script>

<style lang="scss" scoped>
.isis-switch-field {
  margin-top: 30px;
}
</style>
